import React from 'react';
import { useTranslate, Tab, useShowController, usePermissions, ReferenceField } from 'react-admin';
import { Grid, makeStyles, Theme } from '@material-ui/core';
import TagsIcon from '@material-ui/icons/LocalOffer';
import StarIcon from '@material-ui/icons/Star';
import { RibbonIcon } from '~/img';
import { Show, PaperTabbedShowLayout, AsideBase } from '~/layout';
import { Originator, StatusField, TextField } from '~/components';
import { TextField as TextFieldRa, NoPermissions } from '~/components/ra';
import { useRightContentSpacing } from '~/hooks/styles';
import { useRefresh } from 'react-admin';
import AffiliatedObjects from '~/components/AffiliatedObjects';
import { Account } from '~/types/account';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import DetailsTab from '~/resources/accounts/components/DetailsTab';
import BalancesTab from '~/resources/accounts/components/BalancesTab';
import { getResourceByProfileType } from '~/utils/profileType';
import { TransactionsHistory } from '~/components';
import EventLogWidget from '~/components/Events/EventLogWidget';
import AccountExternalLinks from '~/components/AccountExternalLinks';
import { useHistory } from 'react-router-dom';

type StylesProps = {
  spacing: number;
};

const useStyles = makeStyles<Theme, StylesProps>((theme) => ({
  container: {
    paddingLeft: theme.spacing(4),
  },
  rightContent: {
    marginLeft: ({ spacing }) => theme.spacing(spacing),
  },
  marginLeftAside: {
    marginLeft: '24px',
  },
  bottomSpacing: {
    paddingBottom: theme.spacing(2),
  },
  headerSection: {
    '& .sectionActions': {
      opacity: 0.5,
      transition: `opacity ${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
    },
    '&:hover': {
      '& .sectionActions': {
        opacity: 1,
      },
    },
  },
  error: {
    color: theme.palette.error.main,
  },
}));

const resource = 'accounts';

const AccountsShow = (props: any) => {
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const refetch = useRefresh();
  const history = useHistory();
  const translateLabel = (key: string, args?: any): string =>
    translate(`resources.accounts.${key}`, args);
  const t = (key: string) => translate(`resources.accounts.show.details.${key}`);
  // Get the record data
  const showController = useShowController(props);
  const showRecord: Account = showController.record;
  const showResource = showController.resource;

  const spacing = useRightContentSpacing();
  const classes = useStyles({ spacing });

  if (!showRecord) return null;
  if (!permissions) return null;

  const profileResource = getResourceByProfileType(showRecord.profileType);

  const hasAccontsUpdatePermission = permissions?.includes('account.update');
  let hasUpdatePermission = false;
  if (hasAccontsUpdatePermission) {
    hasUpdatePermission = true;
  } else if (profileResource === 'businesses') {
    hasUpdatePermission = permissions?.includes('business.account.update');
  } else if (profileResource === 'individuals') {
    hasUpdatePermission = permissions?.includes('individual.account.update');
  } else {
    hasUpdatePermission = false;
  }

  const currentPathname = history?.location.pathname;
  const detailsPathname = `/${resource}/${showRecord?.id}/show`;
  // const apiIdentityPathname = `/${resource}/${record?.id || ''}/show/apiIdentity`;
  const eventLogPathname = `/${showResource}/${showRecord?.id || ''}/show/eventLog`;
  const transactionsPathname = `/${showResource}/${showRecord?.id || ''}/show/transactions`;

  if (!permissions?.includes('account.view')) return <NoPermissions />;
  return (
    <Show {...props}>
      <PaperTabbedShowLayout
        bottomContent={
          currentPathname === eventLogPathname ? (
            <EventLogWidget
              objectId={showRecord.id}
              objectType='accounts'
              history={props.history}
            />
          ) : null
        }
      >
        <Tab label={translateLabel('tabs.details')}>
          <DetailsTab record={showRecord} />
        </Tab>
        <Tab label={translateLabel('tabs.transactions')} path='transactions'>
          <TransactionsHistory permissions={permissions} resource='accounts' record={showRecord} />
        </Tab>
        <Tab
          label={translateLabel('tabs.balances')}
          path='balances'
          disabled={!permissions?.includes('account.report.balances.list')}
        >
          <BalancesTab record={showRecord} />
        </Tab>
        {permissions?.includes('event.list') && (
          <Tab label={translateLabel('tabs.events')} path='eventLog' />
        )}
      </PaperTabbedShowLayout>
      {currentPathname !== transactionsPathname && (
        <Grid container item xs direction='column' spacing={spacing}>
          <Grid item className={classes.rightContent}>
            <AsideBase>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField label={t('accountNumber')} icon={<AccountBalanceWalletIcon />}>
                    {showRecord?.number}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField label={t('status')} icon={<RibbonIcon />}>
                    <StatusField status={showRecord?.status} />
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <ReferenceField
                    source='productId'
                    reference='products'
                    link={false}
                    addLabel={false}
                    {...props}
                  >
                    <TextFieldRa source='name' label={t('product')} icon={<StarIcon />} />
                  </ReferenceField>
                </Grid>
                <Grid item xs={12}>
                  <TextField label={t('type')} icon={<TagsIcon />}>
                    {showRecord?.type}
                  </TextField>
                </Grid>
              </Grid>
            </AsideBase>
          </Grid>
          <Originator record={showRecord} header={t('owner')} />
          {(profileResource === 'individuals' || profileResource === 'businesses') && (
            <AffiliatedObjects
              id={showRecord.id}
              references={showRecord.references}
              resource={showResource}
              refetch={refetch}
              withAdd={hasUpdatePermission}
              withArchive={hasUpdatePermission}
              withViewAll
            />
          )}

          {currentPathname === detailsPathname && (
            <Grid item className={classes.rightContent}>
              <AccountExternalLinks
                profileId={showRecord.profileId}
                profileType={showRecord.profileType}
                accountId={showRecord.id}
                permissions={permissions}
              />
            </Grid>
          )}
        </Grid>
      )}
    </Show>
  );
};

export default AccountsShow;
